/**
 * Admin agent-access RPC definitions (CLA-583).
 *
 * Person-level per-installation access grants: grant / revoke / list explicit
 * `access_grants` rows so a specific person can reach a specific VPS without
 * membership in the node's customer (or its reseller). All endpoints require
 * admin role via {@link AdminMiddleware}; `granted_by` is stamped from the
 * authenticated admin, never a client field.
 *
 * @since 1.0.0
 */
import { Rpc, RpcGroup } from "@effect/rpc";
import { AccessGrant } from "@fm/db/models/AccessGrant";
import { Schema } from "effect";
import { AdminMiddleware } from "../admin.tags.ts";
import { DatabaseError, Forbidden, RecordNotFoundError, Unauthorized, ValkeyError } from "../errors.ts";

// ============================================================================
// Request Schemas
// ============================================================================

/**
 * Input for `grantAgentAccess` / `revokeAgentAccess` — the target installation
 * and the person receiving (or losing) access.
 *
 * @since 1.0.0
 */
export const AgentAccessGrantInput = Schema.Struct({
  installationId: Schema.NonEmptyTrimmedString,
  personId: Schema.NonEmptyTrimmedString,
});

/**
 * Input for `listAgentAccess` — the installation whose grants to enumerate.
 *
 * @since 1.0.0
 */
export const AgentAccessListInput = Schema.Struct({
  installationId: Schema.NonEmptyTrimmedString,
});

// ============================================================================
// RPC Group
// ============================================================================

/**
 * Admin agent-access RPC group.
 *
 * All endpoints require admin role via AdminMiddleware.
 *
 * @since 1.0.0
 */
export class AgentAccessRpcs extends RpcGroup.make(
  Rpc.make("grantAgentAccess", {
    payload: AgentAccessGrantInput,
    success: AccessGrant.json,
    error: Schema.Union(Unauthorized, Forbidden, RecordNotFoundError, DatabaseError),
  }).middleware(AdminMiddleware),
  Rpc.make("revokeAgentAccess", {
    payload: AgentAccessGrantInput,
    success: Schema.Void,
    error: Schema.Union(Unauthorized, Forbidden, RecordNotFoundError, DatabaseError, ValkeyError),
  }).middleware(AdminMiddleware),
  Rpc.make("listAgentAccess", {
    payload: AgentAccessListInput,
    success: Schema.Array(AccessGrant.json),
    error: Schema.Union(Unauthorized, Forbidden, DatabaseError),
  }).middleware(AdminMiddleware),
).prefix("admin/agent-access/") {}
